import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ActionBar from 'components/text_editor_new/components/action_bar/action_bar';
import AgentAssistanceConfigContext from 'components/contexts/agent_assistance_config_context';
import AITextRewriteControls from 'components/text_editor_new/components/controls/ai_text_rewrite_controls';
import { AITextCompletionContextProvider } from 'components/text_editor_new/plugins/ai/components/ai_text_completion_context';
import {
  AnswerPanelControl,
  BoldItalicUnderlineControls,
  ControlsWrapper,
  Separator,
} from 'components/composer/shared/editor_controls_new';
import { Attachment, Emoji } from 'components/composer/shared/editor_action_buttons_new';
import ChatActions from 'components/composer/chat/chat_actions_new';
import CharacterCount from 'components/text_editor_new/character_count';
import { ChatMessageType } from 'models/chat_message_constants';
import Composer, { Header, NewHeaderText } from 'components/composer/shared/composer_new';
import ComposerContext from 'components/composer/contexts/composer_context';
import ComposerEditor from 'components/composer/shared/composer_editor';
import CreditCardRequestButton from 'components/composer/chat/credit_card_request_button_container';
import createTypingIndicatorPlugin from 'components/composer/chat/typing_indicator_plugin';
import { editorHasNoText } from 'components/text_editor_new/lib/editor_has_no_text';
import { EditorContainer } from 'components/composer/shared/editor_new';
import EditorErrors from 'components/composer/shared/editor_errors';
import { LinkButton } from 'components/composer/inline_links/link_button';
import { LinkEditor } from 'components/composer/inline_links/link_editor';
import SendChat from 'actions/composer/send_chat';
import serializeHtmlExternal from 'components/text_editor_new/lib/serialize_html_external';
import { serializePlaintext } from 'components/text_editor_new/lib/serialize_plaintext';

import useAIComposerOptions from 'components/text_editor_new/plugins/ai/ai_text_completion/use_ai_composer_options';
import useComposer from 'components/composer/shared/use_composer';
import { createSubmitOnEnterPlugin } from 'components/text_editor_new/plugins/submit_on_enter';
import { useCreatePasteAttachmentsPlugin } from 'components/text_editor_new/plugins/create_paste_attachments_plugin';
import useEditor from 'components/composer/shared/use_editor';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import useMessagingEditorPlugins from 'components/text_editor_new/hooks/use_messaging_editor_plugins';
import { useOnTypingRef } from 'components/composer/chat/chat_hooks';
import useChatRichTextEditorPlugins from 'components/text_editor_new/hooks/use_chat_rich_text_editor_plugins';
import withRefreshEditorWhenFlagsChange from 'components/composer/shared/with_refresh_editor_when_flags_change';
import withStaticId from 'components/customer/lib/with_static_id';

export function ChatComposerBase({ includeAIFeatures, requestorId }) {
  const { t } = useTranslation();
  const { composition } = useContext(ComposerContext);

  const composerProps = useComposer({ action: SendChat });
  const editorProps = useEditor('bodyHtml', composerProps, {
    attr: 'text',
    serializer: serializePlaintext,
  });
  const extraPlugins = useChatPlugins();
  const { disableSubmit, readOnly } = useAIComposerOptions();
  editorProps.onSubmit = disableSubmit ? _.noop : editorProps.onSubmit;
  const editorRef = useMessagingEditorPlugins(composerProps, editorProps, extraPlugins);

  const submitButton = (
    <ChatActions
      data-aid="sendButton"
      disableSubmit={disableSubmit}
      editorId="bodyHtml"
      hasAttachments={composition.hasAttachments()}
      onSubmit={editorProps.onSubmit}
    />
  );

  return (
    <Composer
      defaultEditor={editorRef.current}
      defaultEditorId={editorProps.editorId}
      defaultOnChange={editorProps.onChange}
      disableSubmit={disableSubmit}
      excludeErrors
      includeAttachments
      initialHtml={editorProps.initialHtml}
      submitButton={submitButton}
      submitText={t('Send')}
      {...composerProps}
    >
      <Header>
        <Left>
          <NewHeaderText>{t('Chat')}</NewHeaderText>
        </Left>
        <ControlsWrapper />
      </Header>
      <EditorContainer excludeAnswerMenuButton includeAttachments>
        <ComposerEditor
          includeAIFeatures={includeAIFeatures}
          key={composition.id}
          placeholder={t('Type to chat')}
          readOnly={readOnly}
          requestorId={requestorId}
          showFloatingToolbar={false}
          {...editorProps}
        />
      </EditorContainer>

      <MessagingActions>
        <ActionBar>
          <AnswerPanelControl />
          <AITextRewriteControls />
          <Emoji {...editorProps} />
          <CreditCardRequestButton data-aid={'credit-card-request-button'} />
          <Attachment />
        </ActionBar>
        <StyledCharacterCount composerRef={composerProps.composerRef} />
      </MessagingActions>

      <EditorErrors />
    </Composer>
  );
}

ChatComposerBase.propTypes = {
  includeAIFeatures: PropTypes.bool,
  requestorId: PropTypes.string,
};

export function ChatRichTextComposerBase({ includeAIFeatures, requestorId }) {
  const { t } = useTranslation();
  const { composition } = useContext(ComposerContext);

  const composerProps = useComposer({
    action: SendChat,
    options: { createContent: addHtmlMessageType },
  });
  const editorProps = useEditor('bodyHtml', composerProps, { serializer: serializeHtmlExternal });
  const chatPlugins = useChatPlugins();
  const { disableSubmit, readOnly } = useAIComposerOptions();
  editorProps.onSubmit = disableSubmit ? _.noop : editorProps.onSubmit;
  const submitOnEnterRef = useRef();
  useEffect(() => {
    submitOnEnterRef.current = evt => {
      const editor = composerProps.composerRef.current?.editorRefs?.bodyHtml?.current;
      if (editorHasNoText(editor) && !composition.hasAttachments()) {
        evt.preventDefault();
        return;
      }
      editorProps.onSubmit(evt);
    };
  });
  const extraPlugins = useMemo(() => [...chatPlugins, createSubmitOnEnterPlugin(submitOnEnterRef)()], [chatPlugins]);
  const editorRef = useChatRichTextEditorPlugins(composerProps, editorProps, extraPlugins);

  const submitButton = (
    <ChatActions
      data-aid="sendButton"
      disableSubmit={disableSubmit}
      editorId="bodyHtml"
      hasAttachments={composition.hasAttachments()}
      onSubmit={editorProps.onSubmit}
    />
  );

  return (
    <Composer
      defaultEditor={editorRef.current}
      defaultEditorId={editorProps.editorId}
      defaultOnChange={editorProps.onChange}
      disableSubmit={disableSubmit}
      excludeErrors
      includeAttachments
      initialHtml={editorProps.initialHtml}
      submitButton={submitButton}
      submitText={t('Send')}
      {...composerProps}
    >
      <Header>
        <Left>
          <NewHeaderText>{t('Chat')}</NewHeaderText>
        </Left>
        <ControlsWrapper />
      </Header>
      <EditorContainer excludeAnswerMenuButton includeAttachments>
        <ComposerEditor
          attr="bodyHtml"
          includeAIFeatures={includeAIFeatures}
          key={composition.id}
          placeholder={t('Type to chat')}
          readOnly={readOnly}
          requestorId={requestorId}
          showFloatingToolbar
          {...editorProps}
        />
        <LinkEditor hotKey="mod+k" />
      </EditorContainer>

      <MessagingActions>
        <ActionBar>
          <AnswerPanelControl />
          <AITextRewriteControls />
          <BoldItalicUnderlineControls {...editorProps} />
          <>
            <Separator />
            <LinkButton />
          </>
          <Emoji {...editorProps} />
          <CreditCardRequestButton data-aid={'credit-card-request-button'} />
          <Attachment />
        </ActionBar>
        <StyledCharacterCount composerRef={composerProps.composerRef} />
      </MessagingActions>

      <EditorErrors />
    </Composer>
  );
}

ChatRichTextComposerBase.propTypes = {
  includeAIFeatures: PropTypes.bool,
  requestorId: PropTypes.string,
};

function addHtmlMessageType(_editor, _attr, content) {
  return { ...content, messageType: ChatMessageType.HTML };
}

const StyledCharacterCount = styled(CharacterCount)`
  background-color: inherit;
  position: relative;
`;
const MessagingActions = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const Left = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
`;

function ChatComposerWithAIFeatures(props) {
  const agentAssistanceConfig = useContext(AgentAssistanceConfigContext);
  const isFeatureEnabled = useIsFeatureEnabled();
  const includeAIFeatures = !agentAssistanceConfig.disableHeroAI;
  const isRichText = isFeatureEnabled('chatRichTextFormatting');

  const BaseComponent = isRichText ? ChatRichTextComposerBase : ChatComposerBase;
  let composer = <BaseComponent {...props} includeAIFeatures={includeAIFeatures} />;

  if (includeAIFeatures) {
    return (
      <AITextCompletionContextProvider requestorId={props.requestorId}>{composer}</AITextCompletionContextProvider>
    );
  }
  return composer;
}

ChatComposerWithAIFeatures.propTypes = {
  requestorId: PropTypes.string,
};

function useChatPlugins() {
  const onTypingRef = useOnTypingRef();
  const createPasteAttachmentsPlugin = useCreatePasteAttachmentsPlugin();

  return useMemo(() => {
    return [createTypingIndicatorPlugin(onTypingRef), createPasteAttachmentsPlugin()];
  }, [createPasteAttachmentsPlugin, onTypingRef]);
}

export const ChatComposer = withStaticId(withRefreshEditorWhenFlagsChange(ChatComposerWithAIFeatures), 'requestorId');

export default React.memo(ChatComposer);
